import { useUser } from "@/context/useUser";
import {
  DynamicModuleService,
  SchemaService,
  UserPreferencesService,
} from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { ConfigDynamicTableProps } from "@/utils/types/baseTable";
import { ProTable } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Table } from "antd";
import { useEffect, useMemo, useState } from "react";

const ConfigDynamicTable = <T extends object>({
  columns,
  headerTitle,
  rowKey,
  filter,
  toolBarRender,
  selectedRowKeys,
  setSelectedRowKeys,
  tableName,
}: ConfigDynamicTableProps<T> & {
  tableName: string;
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", tableName],
    queryFn: () => SchemaService.getColumnGridData(tableName ?? ""),
    enabled: !columns.length || columns.length === 0,
  });

  // Use useMemo to compute dynamic columns
  const finalColumns = useMemo(() => {
    if (columns.length > 0) {
      return columns;
    }

    return (
      columnGridData?.grid?.map((field: any) => ({
        title: field.field_title,
        dataIndex: field.field_name.toLowerCase(),
      })) ?? []
    );
  }, [columns, columnGridData]);

  const {
    data: moduleData,
    isLoading: moduleLoading,
    refetch,
  } = useQuery({
    queryKey: ["moduleData", tableName, page, pageSize, finalColumns],
    queryFn: () =>
      DynamicModuleService.getGridCoByConfig(
        tableName ?? "",
        page,
        pageSize,
        [
          ...finalColumns
            .filter((column: any) => column.dataIndex)
            .map((column: any) => column.dataIndex),
        ],
        {
          ...filter,
        }
      ),
    enabled: !!tableName && finalColumns.length > 0,
  });
  const paginationConfig = {
    current: page,
    pageSize,
    total: moduleData?.count ?? 0,
    onChange: (newPage: number, newSize: number) => {
      setPage(newPage);
      setPageSize(newSize ?? 10);
    },
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} of ${total}`,
    showSizeChanger: true,
    showQuickJumper: true,
  };

  const { preferencesData, fetchPreferencesData } = useUser();
  const [columnsState, setColumnsState] = useState<any>({});
  const [size, setSize] = useState<"middle" | "small" | "large">("middle");
  const defaultColumnsState = useMemo(() => {
    const state: Record<string, any> = {};
    columns.forEach(col => {
      if (col.dataIndex) {
        state[col.dataIndex as string] = { show: true }; // default show true
      }
    });
    return state;
  }, [columns]);
  useEffect(() => {
    if (preferencesData?.length > 0) {
      setColumnsState(
        preferencesData?.find((item: any) => item.key === tableName)?.json ?? {}
      );
      setSize(
        preferencesData?.find(
          (item: any) => item.key === gridName.settingsTable
        )?.json ?? "middle"
      );
    }
  }, [preferencesData, tableName]);

  const handleColumnsStateChange = async (newState: any) => {
    setColumnsState(newState);
    if (tableName) {
      const payload = {
        key: tableName,
        json:
          Object.keys(newState).length === 0 ? defaultColumnsState : newState,
      };
      await UserPreferencesService.addUserPreferencesGrid(payload);
      fetchPreferencesData();
    }
  };

  async function handleChangeSize(v: any) {
    setSize(v);
    if (tableName) {
      const payload = {
        key: gridName.settingsTable,
        json: v,
      };
      await UserPreferencesService.addUserPreferencesGrid(payload);
      fetchPreferencesData();
    }
  }

  return (
    <ProTable<T>
      rowKey={rowKey}
      rowSelection={{
        selectedRowKeys,
        columnWidth: 60,
        onChange: newSelectedRowKeys =>
          setSelectedRowKeys?.(newSelectedRowKeys),
        selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
      }}
      loading={moduleLoading}
      columns={finalColumns}
      dataSource={
        Array.isArray(moduleData?.data)
          ? moduleData.data
          : Array.isArray(moduleData)
            ? moduleData
            : []
      }
      pagination={paginationConfig}
      toolBarRender={toolBarRender}
      columnsState={{
        value: columnsState,
        onChange: handleColumnsStateChange,
      }}
      size={size}
      onSizeChange={(v: any) => {
        handleChangeSize(v);
      }}
      headerTitle={headerTitle}
      request={() => refetch()}
      search={false}
    />
  );
};

export default ConfigDynamicTable;
